import { adminDashboardApi } from "@/lib/api"
import type {
  AdminCouncilInfo,
  AdminDashboardDTO,
  AdminManufacturerFleetHealth,
  ApiResponseAdminDashboardDTO,
} from "@/gen/api"
import { useQuery } from "@tanstack/react-query"

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function safeNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

function normalizeManufacturerHealth(value: unknown): AdminManufacturerFleetHealth[] {
  if (!Array.isArray(value)) return []

  return value.filter(isRecord).map((item) => ({
    manufacturerId: typeof item["manufacturerId"] === "string" ? item["manufacturerId"] : undefined,
    manufacturerName:
      typeof item["manufacturerName"] === "string" ? item["manufacturerName"] : undefined,
    totalCount: safeNumber(item["totalCount"]),
    onlineCount: safeNumber(item["onlineCount"]),
    offlineCount: safeNumber(item["offlineCount"]),
    healthPercentage: safeNumber(item["healthPercentage"]),
  }))
}

function normalizeAdminDashboard(data: AdminDashboardDTO | undefined): AdminDashboardDTO {
  const councilInfos = Array.isArray(data?.councilInfos)
    ? data.councilInfos.filter(isRecord).map((council) => ({
        ...council,
        userCount: safeNumber(council["userCount"]),
        deviceCount: safeNumber(council["deviceCount"]),
        activeCount: safeNumber(council["activeCount"]),
        offlineCount: safeNumber(council["offlineCount"]),
        alertCount: safeNumber(council["alertCount"]),
        alertDeviceCount: safeNumber(council["alertDeviceCount"]),
        webCount: safeNumber(council["webCount"]),
        androidCount: safeNumber(council["androidCount"]),
        iosCount: safeNumber(council["iosCount"]),
        openLeakCount: safeNumber(council["openLeakCount"]),
        leakCount: safeNumber(council["leakCount"]),
        manufacturerHealth: normalizeManufacturerHealth(council["manufacturerHealth"]),
        leakTrends: Array.isArray(council["leakTrends"])
          ? council["leakTrends"].filter(isRecord)
          : [],
      }))
    : []

  return {
    ...data,
    councilInfos: councilInfos as AdminCouncilInfo[],
  }
}

const useGetAdminDashboard = () => {
  return useQuery({
    queryKey: [useGetAdminDashboard.QUERY_KEY],
    queryFn: async () => {
      const response = await adminDashboardApi.getDashboardStats()
      return normalizeAdminDashboard((response.data as ApiResponseAdminDashboardDTO).data)
    },
  })
}

useGetAdminDashboard.QUERY_KEY = "admin-dashboard"

export default useGetAdminDashboard
